/**
 * Snapshot model for the four headline numbers on the Purchases page.
 * Currency lets the view format the amount item with the active locale's
 * currency rules; the other three are counts and render as plain integers.
 */
export type PurchaseSummary = {
    activeDrafts: number;
    currency: string;
    importedBatches: number;
    pendingInvoices: number;
    totalPurchases: number;
    totalPurchasesTetri: number;
};

export type PurchaseSummaryItemKey =
    'totalPurchases' | 'importedBatches' | 'pendingInvoices' | 'activeDrafts';

/**
 * Iconify identifier shown next to each KPI's title. Kept in a `.ts` file
 * so the View stays render-only (no presentation logic in `.tsx`).
 */
export const PURCHASE_SUMMARY_ITEM_ICON: Record<
    PurchaseSummaryItemKey,
    string
> = {
    totalPurchases: 'solar:wallet-money-bold-duotone',
    importedBatches: 'solar:box-minimalistic-bold-duotone',
    pendingInvoices: 'solar:bill-list-bold-duotone',
    activeDrafts: 'solar:document-add-bold-duotone',
};

/**
 * i18n key (relative to the `pages` namespace) for each KPI's label.
 */
export const PURCHASE_SUMMARY_ITEM_LABEL_KEY: Record<
    PurchaseSummaryItemKey,
    string
> = {
    totalPurchases: 'purchases.widgets.purchaseSummary.totalPurchases',
    importedBatches: 'purchases.widgets.purchaseSummary.importedBatches',
    pendingInvoices: 'purchases.widgets.purchaseSummary.pendingInvoices',
    activeDrafts: 'purchases.widgets.purchaseSummary.activeDrafts',
};

/**
 * Ordered list of item keys so the View renders them in a stable, intended
 * sequence (Total → Batches → Invoices → Drafts) without needing to sort
 * at render time.
 */
export const PURCHASE_SUMMARY_ITEM_ORDER: PurchaseSummaryItemKey[] = [
    'totalPurchases',
    'importedBatches',
    'pendingInvoices',
    'activeDrafts',
];

/**
 * Returns the numeric value backing the given KPI key. Centralised so the
 * View doesn't need to inline a switch/object lookup at render time.
 */
export const getPurchaseSummaryItemValue = (
    summary: PurchaseSummary,
    key: PurchaseSummaryItemKey,
): number =>
    // BOS-162: the money tile reads the integer twin so it can go through
    // `formatGel` like every other amount in the app; the counters are plain
    // integers already.
    key === 'totalPurchases' ? summary.totalPurchasesTetri : summary[key];

/**
 * Whether the KPI's value should be rendered as a currency amount (true) or
 * a plain integer count (false). Today only `totalPurchases` is a money
 * value; the other three are operational counters.
 */
export const isPurchaseSummaryItemMonetary = (
    key: PurchaseSummaryItemKey,
): boolean => key === 'totalPurchases';
